import AddUser from 'assets/images/icons/add-user.svg'
import RemoveUser from 'assets/images/icons/remove-user.svg'
import cx from 'classnames'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { ASSIGN_UNASSIGN, IUseSelectionTpe } from 'features/claims'
import { ClaimOwnerAvatar } from 'features/claims/components/ClaimCommon'
import { useState } from 'react'
import { ButtonGroup, FormControl, InputGroup } from 'react-bootstrap'
import Dropdown, { DropdownProps } from 'react-bootstrap/Dropdown'
import { DropdownItemProps } from 'react-bootstrap/esm/DropdownItem'
import { FaCheck, FaSearch, FaTimes } from 'react-icons/fa'
import InfiniteScroll from 'react-infinite-scroll-component'
import styled, { css } from 'styled-components'
import { longTextTruncate } from 'utils/helper'

interface IMenuItem extends DropdownItemProps {
  userName: string
  onClick?: any
  active?: boolean
}

interface IStyledProps {
  variant?: 'primary' | 'secondary'
  minWidth?: string
  height?: string
}

interface IProps extends IStyledProps, Omit<DropdownProps, 'children'> {
  label: string
  items: IMenuItem[]
  isLoading?: boolean
  isDisabled?: boolean
  searchText: (value: string) => void
  icon?: any
  tooltip?: string | JSX.Element
  emptyState: string | JSX.Element
  labelCount?: { Min: number; Total: number }
  initialLoad: () => void
  close: () => void
  dataLength: number
  next: (t: string) => void
  hasMore: boolean
  assignUnAssign: (value: ASSIGN_UNASSIGN) => void
  selectedMenu?: IUseSelectionTpe
}

export const DropdownWithUserSearch = ({
  variant = 'primary',
  height,
  isLoading,
  label,
  items,
  isDisabled,
  icon,
  searchText,
  emptyState,
  labelCount = { Min: 0, Total: 0 },
  initialLoad,
  close,
  dataLength,
  next,
  hasMore,
  assignUnAssign,
  selectedMenu,
  ...props
}: IProps) => {
  const [search, setSearch] = useState('')

  const callNext = () => {
    next(search)
  }

  const onDropdownToggle = (e: boolean) => {
    if (e) {
      setSearch('')
      initialLoad()
    } else {
      close()
    }
  }

  return (
    <Div variant={variant} height={height}>
      <Dropdown
        className="user-search-btn-group"
        {...props}
        aria-disabled={isDisabled}
        as={ButtonGroup}
        onToggle={onDropdownToggle}
      >
        <>
          <Dropdown.Toggle
            variant={variant}
            split
            disabled={isDisabled || isLoading}
            role="button"
            className={cx('toggle', { disabled: isDisabled })}
          >
            {isLoading ? (
              <>
                {' '}
                <LoadingAnimation /> saving...{' '}
              </>
            ) : (
              <>
                <div>{icon}</div>
                <div>{longTextTruncate(label, labelCount?.Total)}</div>
              </>
            )}
          </Dropdown.Toggle>
        </>

        <Dropdown.Menu className="dropdown user-search-dropdown">
          <InputGroup className="search-box">
            <FaSearch className="search-icon" />
            <FormControl
              value={search}
              className="search-box-input"
              placeholder="Search by name"
              onChange={(e) => {
                setSearch(e.target.value)
                searchText(e.target.value)
              }}
            />
            {search.length > 0 && (
              <FaTimes
                className="clear-icon"
                onClick={() => {
                  setSearch('')
                  searchText('')
                }}
              />
            )}
          </InputGroup>

          <InfiniteScroll
            dataLength={dataLength}
            next={() => callNext()}
            hasMore={hasMore}
            loader={hasMore ? <LoadingAnimation className="infinite-scroll-loader" /> : ''}
            height={200}
          >
            {selectedMenu !== 'Unassign' && (
              <Dropdown.Item onClick={() => assignUnAssign('Unassign')}>
                <div className="unassigned-container">
                  {/* <Avatar className="unassigned-avatar-icon" size="small" initials={<FaUserAlt />} /> */}
                  <img src={RemoveUser} width="24px" height="24px" />
                  <div className="ms-2">Unassign</div>
                </div>
              </Dropdown.Item>
            )}

            {selectedMenu !== 'Assign' && (
              <Dropdown.Item onClick={() => assignUnAssign('Assign')}>
                <div className="unassigned-container">
                  {/* <Avatar className="unassigned-avatar-icon" size="small" initials={<FaUserAlt />} /> */}
                  <img src={AddUser} width="24px" height="24px" />
                  <div className="ms-2">Assign to me</div>
                </div>
              </Dropdown.Item>
            )}

            {items.map(({ userName, onClick, active, ...extraProps }, i) => (
              <Dropdown.Item key={i} onClick={onClick} active={!!active} {...extraProps}>
                {userName && (
                  <div className="search-item-container">
                    <ClaimOwnerAvatar claimOwner={userName} />
                    <div className="ms-2"> {longTextTruncate(userName, labelCount?.Total)} </div>
                  </div>
                )}
                {active && (
                  <div className="check-icon">
                    <FaCheck />
                  </div>
                )}
              </Dropdown.Item>
            ))}
            {items.length <= 0 && emptyState}
          </InfiniteScroll>
        </Dropdown.Menu>
      </Dropdown>
    </Div>
  )
}

const Div = styled.div<IStyledProps>`
  .user-search-btn-group {
    height: ${({ height }) => (height ? height : 'auto')};
    .no-user-found {
      width: 100%;
      margin-top: 0.75rem;
      text-align: center;
      color: ${({ theme }) => theme.colors.grey.neutral};
    }
  }
  .split-primary-btn {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
    gap: 0.25rem;
    cursor: text;
    .unassigned-user {
      display: flex;
      align-items: center;
      .unassigned-avatar-icon {
        margin-right: 8px;
        background-color: ${({ theme }) => theme.colors.grey.light};
        color: ${({ theme }) => theme.colors.grey.lightest};
      }
    }
  }

  .dropdown {
    /* .tooltip-container {
      font-size: 12px;
      font-weight: normal;
      line-height: 1.33;
      letter-spacing: normal;
      text-align: left;
      color: ${({ theme }) => theme.colors.neutral.neutral};
      z-index: 999;
      max-width: 29rem;
      min-width: 20rem;
      white-space: pre-wrap;
    } */
    .infinite-scroll-loader {
      display: block;
      margin: 0 auto;
    }
  }
  .dropdown-toggle {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    box-shadow: none !important;
    border-radius: 8px;
    gap: 0.5rem;
    opacity: 1 !important;
    ${({ theme }) => theme.typography.m.medium}
    ${({ theme, variant }) => {
      switch (variant) {
        case 'primary':
          return css`
            background-color: ${theme.colors.primary.neutral} !important;
            color: ${theme.colors.neutral.lightest} !important;
            border: none !important;
            &::after {
              color: ${theme.colors.neutral.light} !important;
            }
            &:hover {
              background-color: ${theme.colors.primary.dark} !important;
            }

            &:active {
              background-color: ${theme.colors.primary.darker} !important;
            }

            &:disabled {
              color: ${theme.colors.primary.light} !important;
              background-color: ${({ theme }) => theme.colors.primary.lightest} !important;
              border: 1px solid ${theme.colors.primary.lightest};
            }
          `
        case 'secondary':
          return css`
            border: solid 1px ${({ theme }) => theme.colors.primary.neutral} !important;
            background-color: transparent !important;
            color: ${theme.colors.primary.neutral} !important;
            &::after {
              color: ${theme.colors.primary.neutral} !important;
            }
            &:hover {
              outline-offset: -1px;
              outline: 1px solid ${theme.colors.primary.dark}!important;
              background-color: ${theme.colors.primary.lightest} !important;
            }

            &:focus {
              outline-offset: -1px !important;
              outline: 1px solid ${theme.colors.primary.darker}!important;
              background-color: ${theme.colors.primary.lightest}!important;
              color: ${theme.colors.primary.darker}!important;
            }

            &:active {
              background-color: ${theme.colors.primary.lighter}!important;
              outline: 1px solid ${theme.colors.primary.darker}!important;
              outline-offset: -1px;
              color: ${theme.colors.primary.darker}!important;
            }

            &:disabled {
              outline-offset: -1px;
              background-color: transparent;
              color: ${({ theme }) => theme.colors.primary.light}!important;
              outline: 1px solid ${theme.colors.grey.lighter}!important;
            }
          `
      }
    }};
  }
  .dropdown-toggle::after {
    // below border to match up with the design
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border-bottom-left-radius: 20px;
  }

  .dropdown-item.active {
    display: flex;
    align-items: center;
  }

  .search-item-container {
    display: flex;
    align-items: center;
    .avatar-icon {
      margin-right: 0.5rem;
    }
  }
  .unassigned-container {
    display: flex;
    align-items: center;
    .unassigned-avatar-icon {
      margin-right: 8px;
      background-color: ${({ theme }) => theme.colors.grey.light};
      color: ${({ theme }) => theme.colors.grey.lightest};
    }
  }

  .unassigned-container:hover {
    background: ${({ theme }) => theme.colors.tertiary.lighter} !important;
  }
  .dropdown-item {
    ${({ theme }) => theme.typography.s.normal};
    padding: 0.5rem 1rem;
    color: ${({ theme }) => theme.colors.neutral.neutral};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      background-color: ${({ theme }) => theme.colors.tertiary.lighter} !important;
    }
  }
  .dropdown-menu {
    width: 100%;
    min-width: 15rem;
    height: auto;
    max-height: 25rem;
    margin-top: 0.25rem;
    padding: 20px 0;
    overflow: auto;
    overflow-x: hidden;
    border-radius: 8px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.04);
    border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
    background-color: ${({ theme }) => theme.colors.neutral.lightest};
    transform: translate3d(0, 40px, 0px) !important;
    .search-box {
      width: auto;
      margin: 0 1rem 0.5rem;
      input {
        width: 100%;
        height: 2rem;
        font-size: 0.875rem;
        flex: unset !important;
        border-radius: 8px !important;
        padding-left: 2rem;
        color: ${({ theme }) => theme.colors.grey.darker};
      }
      input::placeholder {
        ${({ theme }) => theme.typography.s.normal};
        /* Most modern browsers support this now. */
        color: ${({ theme }) => theme.colors.grey.neutral};
      }
      .search-icon {
        color: ${({ theme }) => theme.colors.grey.neutral};
        position: absolute;
        width: 0.75rem;
        height: 0.75rem;
        left: 0.75rem;
        z-index: 99;
        top: 0.6rem;
      }
      .clear-icon {
        position: absolute;
        width: 0.75rem;
        height: 0.75rem;
        color: ${({ theme }) => theme.colors.grey.neutral};
        right: 0.75rem;
        z-index: 99;
        top: 0.6rem;
        cursor: pointer;
      }
    }

    .check-icon {
      margin-left: 16px;
      color: ${({ theme }) => theme.colors.tertiary.neutral};
      font-size: 0.75rem;
    }
  }
`
